import React, { useEffect, useMemo, useRef, useState } from 'react';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import styled from 'styled-components';

import { months } from 'lib/options/dates';
import useT from 'hooks/useT';

interface MonthItem {
  id: string;
  value: string;
  name: string;
}

const MIN_YEAR = 1900;
const MAX_YEAR = new Date().getFullYear();

// Max days per month; February always allows 29. This form is a lookup input
// (the user's DOB is matched against existing records), not a data-entry form
// that stores whatever is submitted. An impossible date simply won't match,
// so full calendar validation isn't needed — this is just a quick sanity check
// to help users catch obvious typos before hitting the server.
const MAX_DAYS_BY_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function maxDaysForMonth(monthValue: string): number {
  const monthNum = parseInt(monthValue, 10);
  if (!monthNum || monthNum < 1 || monthNum > 12) return 31;
  return MAX_DAYS_BY_MONTH[monthNum - 1];
}

function isDayValidForMonth(monthValue: string, dayValue: number): boolean {
  if (!dayValue || dayValue < 1) return false;
  return dayValue <= maxDaysForMonth(monthValue);
}

const FormWrapper = styled.div`
  margin-top: 1rem;

  .cds--inline-notification {
    max-width: 100%;
    margin: 1rem 0;
  }
`;

const ButtonRow = styled.div`
  margin-top: 1.5rem;
  display: flex;

  button {
    max-width: 100%;
    justify-content: center;
  }
`;

const DateFieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.5rem;

  > * {
    min-width: 0;
  }

  > *:first-child {
    flex: 2 1 160px;
  }

  > *:nth-child(2) {
    flex: 1 1 72px;
  }

  > *:nth-child(3) {
    flex: 1.5 1 96px;
  }

  .mastodon-dropdown,
  .cds--dropdown,
  .cds--list-box {
    width: 100%;
    max-width: 100%;
  }
`;

const DOB_ERROR_I18N_KEY: Record<string, string> = {
  dobMismatch: 'dobMismatch',
  dobSessionExpired: 'dobSessionExpired',
  dobRateLimited: 'dobRateLimited',
  dobProfileDeleted: 'dobProfileDeleted',
};

interface WorkOSDobFormProps {
  errorMessage: string | null;
  onSubmit: (dob: string) => void;
  onStartOver?: () => void;
  processing: boolean;
}

function WorkOSDobForm({
  errorMessage,
  onSubmit,
  onStartOver,
  processing,
}: WorkOSDobFormProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [touched, setTouched] = useState({ day: false, year: false });
  const { locale } = useT();

  const monthItems = useMemo<MonthItem[]>(
    () =>
      months()
        .filter(option => option.value !== '')
        .map(option => ({
          id: option.value,
          value: option.value,
          name: option.i18n ? i18n.getStr(option.name) : option.name,
        })),
    // `locale` isn't read in the body -- it's here only to force a recompute
    // when it changes, since `i18n.getStr()` reads mutable state the linter
    // can't see a dependency on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );
  const selectedMonth = monthItems.find(item => item.value === month) ?? null;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);
  const maxDay = maxDaysForMonth(month);

  const dayInvalid =
    touched.day && day !== '' && !isDayValidForMonth(month, dayNum);
  const yearOutOfRange =
    year.length === 4 && (yearNum < MIN_YEAR || yearNum > MAX_YEAR);
  const yearIncomplete = touched.year && year !== '' && year.length !== 4;
  const yearInvalid = yearOutOfRange || yearIncomplete;
  const yearInvalidText = yearIncomplete
    ? i18n.getStr('components.WorkOSLogin.dobYearIncomplete')
    : i18n.getStr('components.WorkOSLogin.dobYearInvalid', {
        minYear: String(MIN_YEAR),
        maxYear: String(MAX_YEAR),
      });

  const isValidDate = useMemo(() => {
    if (!month || !dayNum || !yearNum) return false;
    if (!isDayValidForMonth(month, dayNum)) return false;
    if (year.length !== 4 || yearNum < MIN_YEAR || yearNum > MAX_YEAR)
      return false;
    return true;
  }, [month, dayNum, yearNum, year]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidDate) {
      const dob = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      onSubmit(dob);
    }
  };

  const errorI18nKey = errorMessage
    ? DOB_ERROR_I18N_KEY[errorMessage] ?? 'dobGenericError'
    : null;
  const translatedError = errorI18nKey
    ? i18n.getStr(`components.WorkOSLogin.${errorI18nKey}`)
    : null;
  const showStartOver =
    errorMessage === 'dobSessionExpired' && typeof onStartOver === 'function';

  return (
    <FormWrapper data-testid='workos-dob-form'>
      <h3 ref={headingRef} tabIndex={-1}>
        {i18n.getStr('components.WorkOSLogin.dobTitle')}
      </h3>
      <p>{i18n.getStr('components.WorkOSLogin.dobSubtitle')}</p>

      {translatedError && (
        <M.InlineNotification
          kind='error'
          title={translatedError}
          hideCloseButton
          data-testid='workos-dob-error'
        />
      )}

      {showStartOver && (
        <ButtonRow>
          <M.Button
            type='button'
            kind='tertiary'
            data-testid='workos-dob-start-over'
            onClick={onStartOver}
            disabled={processing}
            style={{ width: '100%' }}
          >
            {i18n.getStr('components.WorkOSLogin.dobStartOver')}
          </M.Button>
        </ButtonRow>
      )}

      {!showStartOver && (
        <form onSubmit={handleSubmit}>
          <DateFieldRow>
            <M.Dropdown
              key={locale}
              id='workos-dob-month'
              data-testid='workos-dob-month'
              titleText={i18n.getStr('components.WorkOSLogin.dobMonthLabel')}
              label={i18n.getStr('options.selectAMonth')}
              items={monthItems}
              itemToString={(item: MonthItem | null) => (item ? item.name : '')}
              selectedItem={selectedMonth}
              onChange={({
                selectedItem,
              }: {
                selectedItem: MonthItem | null;
              }) => {
                const newMonth = selectedItem?.value ?? '';
                setMonth(newMonth);
                if (day !== '') {
                  setTouched(t => ({ ...t, day: true }));
                }
              }}
              disabled={processing}
            />
            <M.TextInput
              id='workos-dob-day'
              data-testid='workos-dob-day'
              type='text'
              inputMode='numeric'
              labelText={i18n.getStr('components.WorkOSLogin.dobDayLabel')}
              placeholder={i18n.getStr(
                'components.WorkOSLogin.dobDayPlaceholder',
              )}
              value={day}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDay(e.target.value.replace(/\D/g, '').slice(0, 2))
              }
              onBlur={() => setTouched(t => ({ ...t, day: true }))}
              invalid={dayInvalid}
              invalidText={i18n.getStr('components.WorkOSLogin.dobDayInvalid', {
                maxDay: String(maxDay),
              })}
              disabled={processing}
              maxLength={2}
            />
            <M.TextInput
              id='workos-dob-year'
              data-testid='workos-dob-year'
              type='text'
              inputMode='numeric'
              labelText={i18n.getStr('components.WorkOSLogin.dobYearLabel')}
              placeholder={i18n.getStr(
                'components.WorkOSLogin.dobYearPlaceholder',
              )}
              value={year}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setYear(e.target.value.replace(/\D/g, '').slice(0, 4))
              }
              onBlur={() => setTouched(t => ({ ...t, year: true }))}
              invalid={yearInvalid}
              invalidText={yearInvalidText}
              disabled={processing}
              maxLength={4}
            />
          </DateFieldRow>
          <ButtonRow>
            <M.Button
              type='submit'
              kind='primary'
              data-testid='workos-dob-submit'
              disabled={processing || !isValidDate}
              style={{ width: '100%' }}
            >
              {processing ? (
                <M.LoadingInline className='button-loading-icon' />
              ) : (
                i18n.getStr('components.WorkOSLogin.dobSubmitButton')
              )}
            </M.Button>
          </ButtonRow>
        </form>
      )}
    </FormWrapper>
  );
}

export default i18n.renderTranslation()(WorkOSDobForm);
